import {HttpResponse, HttpService} from "./http.service";
import {PublisherModel} from "../models/publisher.model";
import {PublisherByAliasModel} from "../models/publisher-by-alias.model";
import {PublisherSimilarBookListModel} from "../models/publisher/publisher-similar-book-list.model";
import {PublisherApplicationModel} from "../models/publisher/publisher-application.model";
import {PublisherNameModel} from "../models/publisher/publisher-name.model";
import {BookmapBasicInfoModel} from "../models/bookmap-basic-info.model";
import {PaginationModel} from "../models/pagination.model";
import {PublisherCustomProfileModel} from "../models/publisher/publisher-custom-profile.model";
import {IProfileData} from "../components/publisher/create-profile/create-profile";
import {PublisherSimilarBookModel} from "../models/publisher/publisher-similar-book.model";
import {ManuscriptModel} from "../models/manuscript.model";
import {ForeignBookModel} from "../models/foreign-book.model";
import {SimilarBookListModel} from "../models/publisher/similar-book-list.model";
import {CountryModel} from "../models/country.model";
import {EditProfileForm} from "../models/forms/publisher/edit-profile-form.model";
import {AddBookFormModel} from "../models/forms/publisher/add-book-form.model";
import {PublisherBookModel} from "../models/publisher/publisher-book.model";
import moment from "moment";
import {TagModel} from "../models/tag.model";
import {PublisherProposalModel} from "../models/publisher/publisher-proposal.model";
import {PublisherBookPropositionListModel} from "../models/publisher/publisher-book-proposition-list.model";
import {PublisherBookPropositionModel} from "../models/publisher/publisher-book-proposition.model";
import {PublisherSubmittedModel} from "../models/publisher/publisher-submitted.model";
import {MarketBookSearchType, MarketBookSearchTypeToUrl} from "../components/publisher/books/market-search-books";
import {ElasticSearchSort, ElasticSearchType, SearchFilters} from "../components/publisher/books/search-for-elastic";
import {BaseService} from "./base.service";
import {PaginationElasticSearchModel} from "../models/pagination-elastic-search.model";

export const PublisherService = {
    async updateProfile(formValues: EditProfileForm, publisherId: string): Promise<HttpResponse<PublisherModel>> {
        const body = {
            external_links: formValues.links.filter(link => link.url && link.social_likes && link.name).map(link => {
                return {
                    ...link,
                    url: (link.url.search('http') !== 0) ? 'http://' + link.url : link.url
                };
            }),
            name: formValues.name,
            details: {
                address: formValues.address,
                city: formValues.city,
                postal_code: formValues.postalCode,
                nip: formValues.nip,
                phone: formValues.phone,
                phone_prefix: formValues.phonePrefix,
                contact_person: formValues.contactPerson,
                contact_email: formValues.contactEmail,
                country: formValues.country?.iso
            },
            book_languages: formValues.publishingLanguageBooks.map((lang) => lang.code),
            looking_for_editing: formValues.textService1,
            looking_for_translation: formValues.textService2,
            looking_for_reviewing: formValues.textService3,
            looking_for_ghost_writing: formValues.textService4,
            looking_for_illustration: formValues.graphicService1,
            looking_for_covers: formValues.graphicService2,
            looking_for_composition: formValues.graphicService3,
            looking_for_funding: formValues.otherService1,
            looking_for_bookmarketing: formValues.otherService2,
            looking_for_literary_agent: formValues.otherService3,
            looking_for_other: formValues.otherService4 && formValues.otherService4text ? formValues.otherService4text : '',
            annual_number_of_titles: formValues.numberOfBooks ? parseInt(formValues.numberOfBooks) : null,
            average_edition_size: formValues.averageNumberOfBooks ? parseInt(formValues.averageNumberOfBooks) : null,
            looking_for_description: formValues.description2,
            description: formValues.description,
            main_category: formValues.mainCategory,
            is_a_publisher: formValues.publisherType,
            is_an_agency: formValues.agencyType,
            categories_wanted: formValues.categories.map((category) => {
                return {
                    id: category.id,
                    code: (category.code.indexOf('_GENERALCATEGORY') !== -1) ? category.parent_code : category.code
                }
            })
        };

        let response: HttpResponse<PublisherModel>;
        response = await HttpService.patch(`publisher/` + publisherId + '/', body);

        return response;
    },

    async createFirstTimeProfile(
        isPublisher: boolean,
        isAgency: boolean,
        name: string,
        publisherName: PublisherNameModel,
        country: CountryModel
    ) {
        let response: HttpResponse<PublisherModel>;
        const body = {
            is_a_publisher: isPublisher,
            is_an_agency: isAgency,
            details: {
                country: country.iso
            }
        }

        if (publisherName) {
            response = await HttpService.post(`publisher/?pub_id=` + publisherName.id, body);
        } else {
            response = await HttpService.post(`publisher/`, {...body, name});
        }
        return response;
    },

    async updateLogo(
        file: File,
        id: string
    ) {
        const bodyFormData = new FormData();
        bodyFormData.set('logo', file);

        const response = await HttpService.patch(`publisher/${id}/files/`, bodyFormData);

        return response.status;
    },

    async getMe(): Promise<PublisherModel> {
        const response: HttpResponse<PublisherModel> = await HttpService.get('publisher/me/');

        return response.data;
    },

    async getProfile(id: string): Promise<PublisherModel> {
        const response: HttpResponse<PublisherModel> = await HttpService.get('publisher/' + id + '/');

        return response.data;
    },

    async getProfileByAlias(alias: string): Promise<PublisherByAliasModel> {
        const response: HttpResponse<PublisherByAliasModel> = await HttpService.get('publisher/info/?alias=' + alias);

        return response.data;
    },

    async getApplications(search: string = '', status: string[] = [], bookmarked: boolean = undefined, orderField = '', order = '', page = 0, perPage = -1): Promise<PaginationModel<PublisherSimilarBookListModel[]>> {
        let orderUrl = '';
        if (orderField && order) {
            orderUrl = '&ordering=' + ((order === 'desc') ? '-' + orderField : orderField);
        }

        let statusUrl = '';
        status.forEach((stat) => {
            statusUrl += '&status=' + stat;
        });

        let bookmarkedUrl = '';
        if (bookmarked !== null) {
            bookmarkedUrl = '&publisher_bookmarked=' + bookmarked;
        }

        let offset = page * perPage;
        let limit = perPage;

        const response: HttpResponse<PaginationModel<PublisherSimilarBookListModel[]>> = await HttpService.get(`publisher/applications/?offset=${offset}&limit=${limit}&search=` + search + orderUrl + statusUrl + bookmarkedUrl);

        return response?.data || {count: 0, results: []};
    },

    async getApplicationsSearch(
        sortField: ElasticSearchSort,
        sortValue: 'asc'|'desc',
        searchType: ElasticSearchType,
        searchValue: string,
        filters: SearchFilters,
        page = 1,
        perPage = 4,
        statuses: string[] = [],
        bookmarked: boolean = undefined
    ) {
        const body = BaseService.getElasticSearchRequest(
            sortField,
            sortValue,
            searchType,
            searchValue,
            filters,
            page,
            perPage
        );
        body.filter_params['submitted'] = true;
        body.filter_params['statuses'] = statuses;
        body.filter_params['publisher_bookmarked'] = bookmarked;

        const response: HttpResponse<PaginationElasticSearchModel<PublisherSimilarBookListModel[]>> = await HttpService.post(`application/search/`, body);

        return response?.data ?? {count: 0, results: [], next: null, previous: null};
    },

    async getApplication(id: string): Promise<PublisherApplicationModel> {
        const response: HttpResponse<PublisherApplicationModel> = await HttpService.get(`publisher/applications/${id}/`);

        return response.data;
    },

    async getPublisherNames(): Promise<PublisherNameModel[]> {
        const response: HttpResponse<PaginationModel<PublisherNameModel[]>> = await HttpService.get(`publisher/names/?offset=0&limit=-1`);

        return response?.data?.results || [];
    },

    async getApplicationBasicInfo(id: string): Promise<BookmapBasicInfoModel> {
        const response: HttpResponse<any> = await HttpService.get(`application/${id}/bookmap_basic_info/`);

        return response.data;
    },
    async getMarketApplication(search: string = '', status: string[] = [], bookmarked: boolean = undefined, orderField = '', order = '', page = 0, perPage = -1): Promise<PaginationModel<PublisherSimilarBookListModel[]>> {
        let orderUrl = '';
        if (orderField && order) {
            orderUrl = '&ordering=' + ((order === 'desc') ? orderField : '-' + orderField);
        }

        let statusUrl = '';
        status.forEach((stat) => {
            statusUrl += '&status=' + stat;
        });

        let offset = page * perPage;
        let limit = perPage;

        let bookmarkedUrl = '';
        if (bookmarked !== null) {
            bookmarkedUrl = '&publisher_bookmarked=' + bookmarked;
        }

        const response: HttpResponse<PaginationModel<PublisherSimilarBookListModel[]>> = await HttpService.get(`publisher/applications/not-submitted/?offset=${offset}&limit=${limit}&search=` + search + orderUrl + statusUrl + bookmarkedUrl);

        return response?.data || {count: 0, results: []};
    },
    async getMarketApplicationSearch(
        sortField: ElasticSearchSort,
        sortValue: 'asc'|'desc',
        searchType: ElasticSearchType,
        searchValue: string,
        filters: SearchFilters,
        page = 1,
        perPage = 12,
        statuses: string[] = [],
        bookmarked: boolean = undefined
    ) {
        const body = BaseService.getElasticSearchRequest(
            sortField,
            sortValue,
            searchType,
            searchValue,
            filters,
            page,
            perPage
        );
        body.filter_params['submitted'] = false;
        body.filter_params['statuses'] = statuses;
        body.filter_params['publisher_bookmarked'] = bookmarked;

        const response: HttpResponse<PaginationElasticSearchModel<PublisherSimilarBookListModel[]>> = await HttpService.post(`application/search/`, body);

        return response?.data ?? {count: 0, results: [], next: null, previous: null};
    },
    async askForPermission(id: string): Promise<any> {
        const response: HttpResponse<any> = await HttpService.post(`application/${id}/ask_for_access/`, {});

        return true;
    },
    async marketAddToSave(id: string): Promise<any> {
        const response: HttpResponse<any> = await HttpService.post(`application/${id}/like/`, {});

        return true;
    },
    async marketRemoveFromSave(id: string): Promise<any> {
        const response: HttpResponse<any> = await HttpService.post(`application/${id}/unlike/`, {});

        return true;
    },
    async getProfileData(publisherId: any): Promise<PublisherCustomProfileModel> {
        const response: HttpResponse<PublisherCustomProfileModel> = await HttpService.get(`publisher/${publisherId}/profile/`);

        return response.data;
    },
    async updateProfileData(publisherId: any, data: IProfileData): Promise<any> {
        const customProfile = {};

        data.categoriesCalculated.forEach(mainCategory => {
            const totalBooksInMainCategory = Math.round((((data.totalBooks * mainCategory.count) / 100) + Number.EPSILON) * 100) / 100;
            customProfile[mainCategory.code] = totalBooksInMainCategory;

            if (mainCategory.children.length > 0) {
                mainCategory.children.forEach(category => {
                    customProfile[category.code] = Math.round((((totalBooksInMainCategory * category.count) / 100) + Number.EPSILON) * 100) / 100;
                });
            }
        });

        customProfile["TOTAL"] = data.totalBooks;

        const response: HttpResponse<any> = await HttpService.patch(`publisher/${publisherId}/profile/`, { custom_profile: customProfile});

        return !!(response);
    },
    async getPublisherBooks(page = 0, perPage = -1): Promise<PaginationModel<PublisherSimilarBookModel[]>> {
        const offset = page * perPage;
        const limit = perPage;

        const response: HttpResponse<PaginationModel<PublisherSimilarBookModel[]>> = await HttpService.get(`publisher-book/?offset=${offset}&limit=${limit}`);

        return response?.data || {count: 0, results: []};
    },
    async addPublisherBook(title: string, authors: string, isbn: string) {
        const response: HttpResponse<ManuscriptModel> = await HttpService.post('publisher-book/', {
            title, authors, isbn
        });

        return response;
    },
    async editPublisherBook(id: string, title: string, authors: string, isbn: string) {
        const response: HttpResponse<ManuscriptModel> = await HttpService.patch(`publisher-book/${id}/`, {
            title, authors, isbn
        });

        return response;
    },
    async getPublisherBook(id: string): Promise<PublisherSimilarBookModel> {
        const response: HttpResponse<PublisherSimilarBookModel> = await HttpService.get(`publisher-book/${id}/`);

        return response.data;
    },
    async getSimilarBooks(id: string, page: number, perPage: number): Promise<PaginationModel<SimilarBookListModel[]>> {
        let offset = page * perPage;
        let limit = perPage;

        const response: HttpResponse<PaginationModel<SimilarBookListModel[]>> = await HttpService.get(`publisher-book/${id}/similar_manuscripts/?offset=${offset}&limit=${limit}`);

        return response?.data || {count: 0, results: []};
    },
    async getSimilarBooksFromMarket(id: string, page: number, perPage: number): Promise<PaginationModel<SimilarBookListModel[]>> {
        let offset = page * perPage;
        let limit = perPage;

        const response: HttpResponse<PaginationModel<SimilarBookListModel[]>> = await HttpService.get(`publisher-book/${id}/similar_manuscripts_market/?offset=${offset}&limit=${limit}`);

        return response?.data || {count: 0, results: []};
    },
    async uploadFilesBook(id:string, content: File, cover: File, onProgress: Function) {
        return new Promise((resolve, reject) => {
            const bodyFormData = new FormData();

            if (content !== null) {
                bodyFormData.set('content_full', content);
            }

            if (cover !== null) {
                bodyFormData.set('cover', cover);
            }

            const xhr = new XMLHttpRequest();
            xhr.responseType = 'json';

            xhr.upload.addEventListener("progress", function (evt) {
                if (evt.lengthComputable) {
                    onProgress(Math.floor(evt.loaded / evt.total * 1000) / 10);
                }
            }, false);

            xhr.onload = () => {
                if (xhr.response) {
                    resolve(xhr.response);
                }
            };

            xhr.open('PATCH', process.env.REACT_APP_API + `publisher-book/${id}/files/`);
            xhr.setRequestHeader('Authorization', 'Bearer ' + localStorage.getItem('Token'));

            xhr.send(bodyFormData);
        });
    },
    async getForeignBooks(search: string = '', orderField = '', order = '', page = 0, perPage = -1): Promise<PaginationModel<ForeignBookModel[]>> {
        let orderUrl = '';
        if (orderField && orderField === 'submitted_date') {
            orderField = 'publication_date';
        }

        if (orderField && order) {
            orderUrl = '&ordering=' + ((order === 'desc') ? '-' + orderField : orderField);
        }

        const offset = page * perPage;
        const limit = perPage;

        const response: HttpResponse<PaginationModel<ForeignBookModel[]>> = await HttpService.get(`foreign-book/?offset=${offset}&limit=${limit}&search=` + search + orderUrl);

        return response?.data || {count: 0, results: []};
    },
    async getPublisherExampleBooks(page = 0, perPage = 4): Promise<PaginationModel<PublisherSimilarBookModel[]>> {
        const offset = page * perPage;
        const limit = perPage;

        const response: HttpResponse<PaginationModel<PublisherSimilarBookModel[]>> = await HttpService.get(`publisher-example-book/?offset=${offset}&limit=${limit}`);

        return response?.data || {count: 0, results: []};
    },
    async getPublisherExampleBook(id: string): Promise<PublisherSimilarBookModel> {
        const response: HttpResponse<PublisherSimilarBookModel> = await HttpService.get(`publisher-example-book/${id}/`);

        return response.data;
    },
    async getSimilarBooksToExample(id: string, page: number, perPage: number): Promise<PaginationModel<SimilarBookListModel[]>> {
        let offset = page * perPage;
        let limit = perPage;

        const response: HttpResponse<PaginationModel<SimilarBookListModel[]>> = await HttpService.get(`publisher-example-book/${id}/similar_books/?offset=${offset}&limit=${limit}`);

        return response?.data || {count: 0, results: []};
    },
    async getSimilarBooksToExampleFromMarket(id: string, page: number, perPage: number): Promise<PaginationModel<SimilarBookListModel[]>> {
        let offset = page * perPage;
        let limit = perPage;

        const response: HttpResponse<PaginationModel<SimilarBookListModel[]>> = await HttpService.get(`publisher-example-book/${id}/similar_books/?offset=${offset}&limit=${limit}&in_market=True`);

        return response?.data || {count: 0, results: []};
    },
    async foreignBookLinkClicked(id: string) {
        const response: HttpResponse<any> = await HttpService.post(`foreign-book/${id}/book_link/`, {});
        return response;
    },
    async foreignPublisherLinkClicked(id: string) {
        const response: HttpResponse<any> = await HttpService.post(`foreign-book/${id}/publisher_link/`, {});
        return response;
    },
    async addBook(formValues: AddBookFormModel) {
        const body = PublisherService.prepareBookSubmitData(formValues);

        let response: HttpResponse<any>;
        response = await HttpService.post(`published-book/`, body);
        return response;
    },
    async editBook(formValues: AddBookFormModel, bookId: string) {
        const body = PublisherService.prepareBookSubmitData(formValues);

        let response: HttpResponse<any>;
        response = await HttpService.patch(`published-book/${bookId}/`, body);
        return response;
    },
    async getAllMyBooks(page = 0, perPage = 4): Promise<PaginationModel<PublisherBookModel[]>> {
        const offset = page * perPage;
        const limit = perPage;

        const response: HttpResponse<PaginationModel<PublisherBookModel[]>> = await HttpService.get(`published-book/?offset=${offset}&limit=${limit}`);

        return response?.data || {count: 0, results: []};
    },
    async getAllMyBooksSearch(
        sortField: ElasticSearchSort,
        sortValue: 'asc'|'desc',
        searchType: ElasticSearchType,
        searchValue: string,
        filters: SearchFilters,
        page = 0,
        perPage = 4
    ) {
        const body = BaseService.getElasticSearchRequest(
            sortField,
            sortValue,
            searchType,
            searchValue,
            filters,
            page,
            perPage
        );
        body.filter_params['statuses'] = [];

        const response: HttpResponse<PaginationElasticSearchModel<PublisherBookModel[]>> = await HttpService.post(`published-book/search/`, body);

        return response?.data ?? {count: 0, results: [], next: null, previous: null};
    },
    async getBook(id: string): Promise<PublisherBookModel> {
        const response: HttpResponse<PublisherBookModel> = await HttpService.get(`published-book/${id}/`);

        return response.data;
    },
    async uploadFilesToBook(file: File | string, cover: File | string, id: string, onProgress: Function): Promise<PublisherBookModel> {
        return new Promise((resolve, reject) => {
            const bodyFormData = new FormData();
            if (file !== null) {
                bodyFormData.set('content_full', file);
            }

            if (cover !== null) {
                bodyFormData.set('cover', cover);
            }

            const xhr = new XMLHttpRequest();
            xhr.responseType = 'json';

            xhr.upload.addEventListener("progress", function (evt) {
                if (evt.lengthComputable) {
                    onProgress(Math.floor(evt.loaded / evt.total * 1000) / 10);
                }
            }, false);

            xhr.onload = () => {
                if (xhr.response) {
                    resolve(xhr.response);
                }
            };

            xhr.open('PATCH', process.env.REACT_APP_API + `published-book/${id}/files/`);
            xhr.setRequestHeader('Authorization', 'Bearer ' + localStorage.getItem('Token'));

            xhr.send(bodyFormData);
        });
    },
    async getProposals(id: string, offset: number, limit: number, status: string[]): Promise<PublisherProposalModel[]> {
        let statusUrl = '';
        status.forEach((stat) => {
            statusUrl += '&status=' + stat;
        });

        const response: HttpResponse<PaginationModel<PublisherProposalModel[]>> = await HttpService.get(`published-book/${id}/proposals/?offset=${offset}&limit=${limit}&ordering=-matching${statusUrl}`);

        return response?.data?.results || [];
    },
    async getSubmitted(id: string): Promise<PublisherSubmittedModel[]> {
        const response: HttpResponse<PaginationModel<PublisherSubmittedModel[]>> = await HttpService.get(`published-book/${id}/submitted/?offset=0&limit=-1`);

        return response?.data?.results || [];
    },
    async submitToPublisher(id: string): Promise<any> {
        const response: HttpResponse<any> = await HttpService.post(`publisher-application/${id}/submit/`, {});

        return response.status;
    },
    async getBooksPropositions(search: string = '', status: string[] = [], bookmarked: boolean = undefined, orderField = '', order = '', page = 0, perPage = -1): Promise<PaginationModel<PublisherBookPropositionListModel[]>> {
        let orderUrl = '';
        if (orderField && order) {
            orderUrl = '&ordering=' + ((order === 'desc') ? '-' + orderField : orderField);
        }

        let statusUrl = '';
        status.forEach((stat) => {
            statusUrl += '&status=' + stat;
        });

        let bookmarkedUrl = '';
        if (bookmarked !== null) {
            bookmarkedUrl = '&publisherto_bookmarked=' + bookmarked;
        }

        let offset = page * perPage;
        let limit = perPage;

        const response: HttpResponse<PaginationModel<PublisherBookPropositionListModel[]>> = await HttpService.get(`publisher/published_book_applications/?offset=${offset}&limit=${limit}&search=` + search + orderUrl + statusUrl + bookmarkedUrl);

        return response?.data || {count: 0, results: []};
    },
    async getBooksPropositionsSearch(
        sortField: ElasticSearchSort,
        sortValue: 'asc'|'desc',
        searchType: ElasticSearchType,
        searchValue: string,
        filters: SearchFilters,
        page = 0,
        perPage = 4,
        statuses: string[] = [],
        bookmarked: boolean = undefined
    ) {
        const body = BaseService.getElasticSearchRequest(
            sortField,
            sortValue,
            searchType,
            searchValue,
            filters,
            page,
            perPage
        );
        body.filter_params['submitted'] = true;
        body.filter_params['statuses'] = statuses;
        body.filter_params['publisher_bookmarked'] = bookmarked;

        const response: HttpResponse<PaginationElasticSearchModel<PublisherBookPropositionListModel[]>> = await HttpService.post(`publisher-application/search/`, body);

        return response?.data ?? {count: 0, results: [], next: null, previous: null};
    },
    async getBookProposition(id: string): Promise<PublisherBookPropositionModel> {
        const response: HttpResponse<PublisherBookPropositionModel> = await HttpService.get(`publisher/published-book-applications/${id}/`);

        return response.data;
    },
    async bookPropositionAddBookmark(id: string): Promise<HttpResponse<any>> {
        const response: HttpResponse<any> = await HttpService.post(`publisher-application/${id}/bookmark/`, {});

        return response;
    },
    async bookPropositionPubWantToPublish(id: string): Promise<HttpResponse<any>> {
        const response: HttpResponse<any> = await HttpService.post(`publisher-application/${id}/publish/`, {});

        return response;
    },
    async bookPropositionPubDoesntWantToPublish(id: string): Promise<HttpResponse<any>> {
        const response: HttpResponse<any> = await HttpService.post(`publisher-application/${id}/no_publish/`, {});

        return response;
    },
    async pubFromDoesntWantToPublish(id: string): Promise<HttpResponse<any>> {
        const response: HttpResponse<any> = await HttpService.post(`publisher-application/${id}/reject/`, {});

        return response;
    },
    async pubFromWantToPublish(id: string): Promise<HttpResponse<any>> {
        const response: HttpResponse<any> = await HttpService.post(`publisher-application/${id}/accept/`, {});

        return response;
    },
    async success(id: string): Promise<HttpResponse<any>> {
        const response: HttpResponse<any> = await HttpService.post(`publisher-application/${id}/success/`, {});

        return response;
    },
    async fail(id: string): Promise<HttpResponse<any>> {
        const response: HttpResponse<any> = await HttpService.post(`publisher-application/${id}/fail/`, {});

        return response;
    },
    async confirmPublisher(id: string): Promise<any> {
        const response: HttpResponse<any> = await HttpService.post(`publisher-application/${id}/confirm_publisher_request/`, {});

        return response.status;
    },
    async declinePublisher(id: string): Promise<any> {
        const response: HttpResponse<any> = await HttpService.post(`publisher-application/${id}/decline_publisher_request/`, {});

        return response.status;
    },
    async getBookMarketApplication(search: string = '', searchType: MarketBookSearchType, status: string[] = [], bookmarked: boolean = undefined, orderField = '', order = '', page = 0, perPage = -1): Promise<PaginationModel<PublisherBookPropositionListModel[]>> {
        let orderUrl = '';
        if (orderField && order) {
            orderUrl = '&ordering=' + ((order === 'desc') ? orderField : '-' + orderField);
        }

        let statusUrl = '';
        status.forEach((stat) => {
            statusUrl += '&status=' + stat;
        });

        let offset = page * perPage;
        let limit = perPage;

        let bookmarkedUrl = '';
        if (bookmarked !== null) {
            bookmarkedUrl = '&publisherto_bookmarked=' + bookmarked;
        }

        let searchUrl = '';
        if (search && searchType) {
            if (searchType === MarketBookSearchType.searchTypeGeneral) {
                searchUrl = `&search=${search}`
            } else {
                searchUrl = `&${MarketBookSearchTypeToUrl[searchType]}=${search}`
            }
        }

        const response: HttpResponse<PaginationModel<PublisherBookPropositionListModel[]>> = await HttpService.get(`publisher/publisher-applications/not-submitted/?offset=${offset}&limit=${limit}` + searchUrl + orderUrl + statusUrl + bookmarkedUrl);
        return response?.data || {count: 0, results: []};
    },
    async getBookMarketApplicationSearch(
        sortField: ElasticSearchSort,
        sortValue: 'asc'|'desc',
        searchType: ElasticSearchType,
        searchValue: string,
        filters: SearchFilters,
        page = 0,
        perPage = 4,
        statuses: string[] = [],
        bookmark: boolean = undefined
    ) {
        const body = BaseService.getElasticSearchRequest(
            sortField,
            sortValue,
            searchType,
            searchValue,
            filters,
            page,
            perPage
        );
        body.filter_params['submitted'] = false;
        body.filter_params['statuses'] = statuses;
        body.filter_params['publisher_bookmarked'] = bookmark;

        const response: HttpResponse<PaginationElasticSearchModel<PublisherBookPropositionListModel[]>> = await HttpService.post(`publisher-application/search/`, body);

        return response?.data ?? {count: 0, results: [], next: null, previous: null};
    },
    async bookMarketAddToSave(id: string): Promise<any> {
        const response: HttpResponse<any> = await HttpService.post(`publisher-application/${id}/like/`, {});

        return true;
    },
    async bookMarketRemoveFromSave(id: string): Promise<any> {
        const response: HttpResponse<any> = await HttpService.post(`publisher-application/${id}/unlike/`, {});

        return true;
    },
    async bookMarketAskForPermission(id: string): Promise<any> {
        const response: HttpResponse<any> = await HttpService.post(`publisher-application/${id}/ask_for_access/`, {});

        return true;
    },
    prepareBookSubmitData(formValues: AddBookFormModel) {
        const tagsReady: TagModel[] = [];
        formValues.tags.forEach((tag) => {
            if (typeof tag === 'string') {
                tagsReady.push({
                    id: null,
                    name: tag,
                    tag_type: 'tag'
                });
            } else {
                tagsReady.push(tag);
            }
        });

        return {
            original_title: formValues.title,
            en_title: formValues.titleEn,
            authors: formValues.author,
            isbn: formValues.isbn,
            tags: tagsReady,
            categories: formValues.categories.map((category) => {
                return {
                    id: category.id,
                    code: (category.code.indexOf('_GENERALCATEGORY') !== -1) ? category.parent_code : category.code
                }
            }),
            main_category: formValues.mainCategory,
            book_language: formValues.originalLang?.code || '',
            description: formValues.description,
            summary: formValues.summary,
            awards: formValues.prizes.filter((prize) => prize.name),
            phase: formValues.bookStatus,
            published_at_the_market: formValues.publishMarket,
            looking_for_editor: formValues.editors,
            looking_for_corrector: formValues.corrector,
            looking_for_illustration: formValues.graphic,
            looking_for_reviewing: formValues.reviewer,
            saledata: {
                copies: formValues.numberOfBooks ? parseInt(formValues.numberOfBooks) : null,
                publish_date: formValues.releaseDate ? moment(formValues.releaseDate).format('YYYY-MM-DD') : null,
                editions: formValues.numberOfReleases ? parseInt(formValues.numberOfReleases) : null,
                sold_copies: formValues.numberOfSold ? parseInt(formValues.numberOfSold) : null
            },
            translation_wishes: formValues.langToLang.filter((lang) => {
                return lang.fromLang && lang.toLang;
            }).map((lang) => ({
                language_from: lang.fromLang,
                language_to: lang.toLang,
            })) || [],
            review: formValues.review,
            fragment: formValues.partOfBook,
            table_of_contents: formValues.tableOfContents,
            countries_sold_to: formValues.countryWithLicense.map((country) => country.iso) || [],
            foreign_publisher_countries: formValues.searchPublisherCountries.map((country) => country.iso) || [],
            translations: formValues.translatedTo.map((trans) => ({language: trans.code})) || [],
            // searchPublisherCountries
        }
    }
}
